import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Mail, Clock, Lock, UserCheck, FileText } from 'lucide-react';

const PrivacyPolicy = () => {
  const { language } = useLanguage();

  const content = {
    pt: {
      title: 'Política de Privacidade',
      lastUpdate: 'Última atualização: Janeiro de 2025',
      backHome: 'Voltar à página inicial',
      intro: 'A Contabilidade e Fiscalidade, Lda ("nós", "nosso" ou "empresa") está empenhada em proteger a privacidade dos seus dados pessoais. Esta Política de Privacidade explica como recolhemos, utilizamos e protegemos as informações que nos fornece através do nosso website, em conformidade com o Regulamento Geral sobre a Proteção de Dados (RGPD) - Regulamento (UE) 2016/679.',
      sections: [
        {
          icon: FileText,
          title: '1. Dados Pessoais Recolhidos',
          content: 'Através do formulário de contacto disponível no nosso website, recolhemos os seguintes dados pessoais:',
          list: [
            'Nome completo',
            'Endereço de correio eletrónico (email)',
            'Número de telefone (opcional)',
            'Mensagem/pedido de informação'
          ]
        },
        {
          icon: UserCheck,
          title: '2. Finalidade do Tratamento',
          content: 'Os dados pessoais recolhidos são tratados exclusivamente para as seguintes finalidades:',
          list: [
            'Responder aos seus pedidos de contacto e solicitações de orçamento',
            'Prestar esclarecimentos sobre os nossos serviços',
            'Dar seguimento a questões relacionadas com contabilidade e fiscalidade',
            'Estabelecer contacto comercial, caso solicitado'
          ]
        },
        {
          icon: Shield,
          title: '3. Base Legal do Tratamento',
          content: 'O tratamento dos seus dados pessoais tem como base legal:',
          list: [
            'O seu consentimento, prestado de forma livre, específica, informada e inequívoca, ao submeter o formulário de contacto (artigo 6.º, n.º 1, alínea a) do RGPD)',
            'A execução de diligências pré-contratuais a seu pedido, nomeadamente a elaboração de orçamentos e propostas de serviços (artigo 6.º, n.º 1, alínea b) do RGPD)'
          ]
        },
        {
          icon: Clock,
          title: '4. Período de Conservação dos Dados',
          content: 'Os seus dados pessoais serão conservados pelo período estritamente necessário para as finalidades indicadas:',
          list: [
            'Dados de contacto para resposta a pedidos: até 12 meses após o último contacto, salvo se existir relação contratual',
            'Dados relacionados com propostas comerciais não aceites: até 6 meses após a apresentação da proposta',
            'Caso se estabeleça uma relação contratual, os dados serão conservados pelo período legalmente exigido para cumprimento de obrigações fiscais e contabilísticas (10 anos)'
          ],
          extra: 'Findo o período de conservação, os dados serão eliminados de forma segura.'
        },
        {
          icon: Lock,
          title: '5. Partilha de Dados com Terceiros',
          content: 'Garantimos que os seus dados pessoais NÃO são partilhados com terceiros para fins comerciais ou de marketing. Os seus dados apenas poderão ser comunicados a:',
          list: [
            'Autoridades públicas, quando exigido por lei',
            'Prestadores de serviços essenciais ao funcionamento do website (alojamento), vinculados por acordos de confidencialidade'
          ],
          extra: 'Não vendemos, alugamos ou cedemos os seus dados pessoais a terceiros.'
        },
        {
          icon: UserCheck,
          title: '6. Os Seus Direitos',
          content: 'Nos termos do RGPD, tem os seguintes direitos relativamente aos seus dados pessoais:',
          list: [
            'Direito de acesso: obter confirmação sobre o tratamento dos seus dados e aceder aos mesmos',
            'Direito de retificação: solicitar a correção de dados inexatos ou incompletos',
            'Direito ao apagamento: solicitar a eliminação dos seus dados ("direito a ser esquecido")',
            'Direito à limitação do tratamento: restringir o tratamento em determinadas circunstâncias',
            'Direito de oposição: opor-se ao tratamento dos seus dados',
            'Direito à portabilidade: receber os seus dados num formato estruturado e de uso corrente'
          ],
          extra: 'Para exercer qualquer destes direitos, contacte-nos através dos meios indicados abaixo.'
        },
        {
          icon: Mail,
          title: '7. Contacto para Proteção de Dados',
          content: 'Para quaisquer questões relacionadas com a proteção dos seus dados pessoais, ou para exercer os seus direitos, pode contactar-nos através de:',
          contact: {
            company: 'Contabilidade e Fiscalidade, Lda',
            email: 'privacidade@cflda.pt',
            address: 'Rua Prudêncio Franco da Trindade 4, 2655-344 Ericeira',
            phone: '911 000 000'
          }
        },
        {
          icon: Shield,
          title: '8. Direito de Reclamação',
          content: 'Caso considere que o tratamento dos seus dados pessoais viola o RGPD, tem o direito de apresentar reclamação junto da autoridade de controlo competente:',
          contact: {
            authority: 'Comissão Nacional de Proteção de Dados (CNPD)',
            website: 'www.cnpd.pt'
          }
        }
      ]
    },
    en: {
      title: 'Privacy Policy',
      lastUpdate: 'Last updated: January 2025',
      backHome: 'Back to homepage',
      intro: 'Contabilidade e Fiscalidade, Lda ("we", "our" or "company") is committed to protecting the privacy of your personal data. This Privacy Policy explains how we collect, use and protect the information you provide through our website, in compliance with the General Data Protection Regulation (GDPR) - Regulation (EU) 2016/679.',
      sections: [
        {
          icon: FileText,
          title: '1. Personal Data Collected',
          content: 'Through the contact form available on our website, we collect the following personal data:',
          list: [
            'Full name',
            'Email address',
            'Phone number (optional)',
            'Message/information request'
          ]
        },
        {
          icon: UserCheck,
          title: '2. Purpose of Processing',
          content: 'The personal data collected is processed exclusively for the following purposes:',
          list: [
            'Respond to your contact requests and quote requests',
            'Provide clarifications about our services',
            'Follow up on accounting and tax matters',
            'Establish commercial contact, if requested'
          ]
        },
        {
          icon: Shield,
          title: '3. Legal Basis for Processing',
          content: 'The processing of your personal data is based on:',
          list: [
            'Your consent, given freely, specifically, informedly and unambiguously when submitting the contact form (Article 6(1)(a) GDPR)',
            'The execution of pre-contractual measures at your request, namely the preparation of quotes and service proposals (Article 6(1)(b) GDPR)'
          ]
        },
        {
          icon: Clock,
          title: '4. Data Retention Period',
          content: 'Your personal data will be kept for the period strictly necessary for the stated purposes:',
          list: [
            'Contact data for responding to requests: up to 12 months after last contact, unless a contractual relationship exists',
            'Data related to unaccepted commercial proposals: up to 6 months after proposal presentation',
            'If a contractual relationship is established, data will be kept for the legally required period for tax and accounting obligations (10 years)'
          ],
          extra: 'After the retention period, data will be securely deleted.'
        },
        {
          icon: Lock,
          title: '5. Data Sharing with Third Parties',
          content: 'We guarantee that your personal data is NOT shared with third parties for commercial or marketing purposes. Your data may only be communicated to:',
          list: [
            'Public authorities, when required by law',
            'Essential service providers for website operation (hosting), bound by confidentiality agreements'
          ],
          extra: 'We do not sell, rent or transfer your personal data to third parties.'
        },
        {
          icon: UserCheck,
          title: '6. Your Rights',
          content: 'Under GDPR, you have the following rights regarding your personal data:',
          list: [
            'Right of access: obtain confirmation about the processing of your data and access it',
            'Right to rectification: request correction of inaccurate or incomplete data',
            'Right to erasure: request deletion of your data ("right to be forgotten")',
            'Right to restriction: restrict processing in certain circumstances',
            'Right to object: object to the processing of your data',
            'Right to portability: receive your data in a structured, commonly used format'
          ],
          extra: 'To exercise any of these rights, contact us through the means indicated below.'
        },
        {
          icon: Mail,
          title: '7. Data Protection Contact',
          content: 'For any questions related to the protection of your personal data, or to exercise your rights, you can contact us at:',
          contact: {
            company: 'Contabilidade e Fiscalidade, Lda',
            email: 'privacidade@cflda.pt',
            address: 'Rua Prudêncio Franco da Trindade 4, 2655-344 Ericeira',
            phone: '911 000 000'
          }
        },
        {
          icon: Shield,
          title: '8. Right to Complaint',
          content: 'If you consider that the processing of your personal data violates the GDPR, you have the right to lodge a complaint with the competent supervisory authority:',
          contact: {
            authority: 'Comissão Nacional de Proteção de Dados (CNPD)',
            website: 'www.cnpd.pt'
          }
        }
      ]
    }
  };

  const t = content[language];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            {t.backHome}
          </Link>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">{t.title}</h1>
          <p className="text-slate-500 mt-2">{t.lastUpdate}</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Introduction */}
        <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-slate-100 mb-8">
          <p className="text-slate-600 leading-relaxed">{t.intro}</p>
        </div>

        {/* Sections */}
        <div className="space-y-6">
          {t.sections.map((section, index) => {
            const IconComponent = section.icon;
            return (
              <div 
                key={index}
                className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-slate-100"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <IconComponent className="w-5 h-5 text-teal-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-slate-900 pt-1">{section.title}</h2>
                </div>
                
                <p className="text-slate-600 mb-4 leading-relaxed">{section.content}</p>
                
                {section.list && (
                  <ul className="space-y-2 mb-4">
                    {section.list.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-slate-600">
                        <span className="w-1.5 h-1.5 bg-teal-500 rounded-full mt-2 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
                
                {section.extra && (
                  <p className="text-slate-600 bg-slate-50 p-4 rounded-lg">{section.extra}</p>
                )}
                
                {section.contact && (
                  <div className="bg-teal-50 p-4 rounded-lg mt-4">
                    {section.contact.company && (
                      <p className="font-semibold text-slate-900">{section.contact.company}</p>
                    )}
                    {section.contact.authority && (
                      <p className="font-semibold text-slate-900">{section.contact.authority}</p>
                    )}
                    {section.contact.email && (
                      <p className="text-teal-600">
                        <a href={`mailto:${section.contact.email}`} className="hover:underline">
                          {section.contact.email}
                        </a>
                      </p>
                    )}
                    {section.contact.address && (
                      <p className="text-slate-600">{section.contact.address}</p>
                    )}
                    {section.contact.phone && (
                      <p className="text-slate-600">{section.contact.phone}</p>
                    )}
                    {section.contact.website && (
                      <p className="text-teal-600">
                        <a 
                          href={`https://${section.contact.website}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="hover:underline"
                        >
                          {section.contact.website}
                        </a>
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
